import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import SimpleBar from "simplebar-react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { MetaTitle } from "../../common/data/MetaTitle";
import { MonthFormat } from "../../common/constant/Constants";
import { GetApi } from "../../common/api/GetApi"; // adjust path/name to your GET helper
import { PostApi } from "../../common/api/PostApi";
import { ParseTime } from "../../common/js/ParseTime";
import { ConvertSecondsToTime } from "../../common/js/ConvertSecondsToTime";
import { GetAmPmDateTime } from "../../common/js/ConvertDateTimeToAmPm";
import { setLoaderVisible } from "../../redux/reducers/ChildReducer/LoaderReducer";

dayjs.extend(customParseFormat);

// hr_status: 2 = Approved, 3 = Pending, 4 = Rejected, 1 = not submitted
const HR_STATUS = {
  2: { label: "Approved", color: "success" },
  3: { label: "Pending", color: "primary" },
  4: { label: "Rejected", color: "danger" },
};

const secondsToTime = (sec = 0) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const timeToSeconds = (value) => {
  if (!value) return 0;
  const [h, m] = value.split(":").map(Number);
  return (h || 0) * 3600 + (m || 0) * 60;
};

// Accepts H:MM, HH:MM, HH:MM:SS -> "HH:MM", else null
const normalizeTime = (value) => {
  const m = String(value || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};

const csvEscape = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const parseCsv = (text) => {
  const src = text.replace(/^\uFEFF/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else inQuotes = false;
      } else cell += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") {
      row.push(cell);
      cell = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      cell = "";
      rows.push(row);
      row = [];
    } else cell += c;
  }
  if (cell !== "" || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
};

const getDayStatus = (r) => {
  if (r.is_holiday)
    return { label: r.holiday_name || "Holiday", color: "info" };
  if (r.is_week_off) return { label: "Week Off", color: "secondary" };
  if (r.is_leave_applied) {
    const t = r.leave_application?.leave_type_name || "Leave";
    return {
      label: `${t} (${r.is_leave_approved ? "Approved" : "Pending"})`,
      color: "warning",
    };
  }
  if (r.is_attendance_not_marked)
    return { label: "Not Marked", color: "danger" };
  if (r.is_present && !r.is_properly_closed)
    return { label: "Not Checked Out", color: "warning" };
  if (r.is_present) return { label: "Present", color: "success" };
  return { label: "Absent", color: "danger" };
};

const currentMonth = () => format(new Date(), "yyyy-MM");

const AttendanceBulkEdit = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [thisMonth, setThisMonth] = useState(currentMonth());
  const [records, setRecords] = useState([]);
  const [edits, setEdits] = useState({}); // { [id]: { selected, time, notes } }
  const [commonNotes, setCommonNotes] = useState("");
  const [commonTime, setCommonTime] = useState("");
  const [commonDirty, setCommonDirty] = useState(false); // typed but not yet applied
  const [submitting, setSubmitting] = useState(false);

  const [notes_modal, setNotes_modal] = useState(false);
  const [notesList, setNotesList] = useState([]);
  const [notesDate, setNotesDate] = useState(null);

  // A row can be edited only if HR hasn't acted on it and no leave covers it.
  const canEdit = (r) => r.is_editable && !r.is_leave_applied;

  const fetchRecords = useCallback(async () => {
    dispatch(setLoaderVisible(true));
    try {
      const Api = await GetApi(
        `/attendance/bulk-edit/list/?thisMonth=${thisMonth}`,
        false,
      );
      if (Api?.status === 200) {
        setRecords(Api?.data?.data || []);
        setEdits({});
      } else {
        setRecords([]);
        toast.error(
          Api?.data?.message || Api?.data?.detail || "Failed to load",
        );
      }
    } catch (e) {
      setRecords([]);
      toast.error("Failed to load records");
    }
    dispatch(setLoaderVisible(false));
  }, [thisMonth, dispatch]);

  useEffect(() => {
    if (thisMonth) fetchRecords();
  }, [fetchRecords]);

  const editableRows = useMemo(() => records.filter(canEdit), [records]);
  const selectedIds = useMemo(
    () =>
      Object.keys(edits)
        .filter((id) => edits[id]?.selected)
        .map(Number),
    [edits],
  );
  const allSelected =
    editableRows.length > 0 && selectedIds.length === editableRows.length;

  // Priority: applied common values > existing edit > full-day duration
  const defaultTime = (r) =>
    commonTime && !commonDirty
      ? commonTime
      : secondsToTime(
          r.edited_checked_in_hr_in_seconds > 0
            ? r.edited_checked_in_hr_in_seconds
            : r.maximum_full_day_work_duration,
        );
  const defaultNotes = () => (!commonDirty ? commonNotes : "");

  const toggleRow = (r) => {
    if (!canEdit(r)) return;
    setEdits((prev) => {
      const cur = prev[r.id];
      if (cur?.selected)
        return { ...prev, [r.id]: { ...cur, selected: false } };
      return {
        ...prev,
        [r.id]: {
          selected: true,
          time: cur?.time ?? defaultTime(r),
          notes: cur?.notes ?? defaultNotes(),
        },
      };
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setEdits((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach(
          (id) => (next[id] = { ...next[id], selected: false }),
        );
        return next;
      });
      return;
    }
    setEdits((prev) => {
      const next = { ...prev };
      editableRows.forEach((r) => {
        next[r.id] = {
          selected: true,
          time: prev[r.id]?.time ?? defaultTime(r),
          notes: prev[r.id]?.notes ?? defaultNotes(),
        };
      });
      return next;
    });
  };

  const setField = (id, field, value) =>
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  // Set common hours + notes onto every selected row
  const applyCommon = () => {
    if (selectedIds.length === 0) {
      toast.warning("Select at least one record");
      return;
    }
    if (!commonTime && !commonNotes.trim()) {
      toast.warning("Enter common hours or notes");
      return;
    }
    if (commonTime && timeToSeconds(commonTime) <= 0) {
      toast.warning("Enter valid common hours");
      return;
    }
    setEdits((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[id] = {
          ...next[id],
          ...(commonTime ? { time: commonTime } : {}),
          ...(commonNotes.trim() ? { notes: commonNotes.trim() } : {}),
        };
      });
      return next;
    });
    setCommonDirty(false);
    toast.success(`Applied to ${selectedIds.length} record(s)`);
  };

  // ---------- Export sample ----------
  const handleExportSample = () => {
    if (editableRows.length === 0) {
      toast.warning("No editable records to export");
      return;
    }
    const header = [
      "id",
      "date",
      "day",
      "day_status",
      "checked_hours",
      "edited_hours",
      "notes",
    ];
    const lines = [header.join(",")];
    editableRows.forEach((r) => {
      lines.push(
        [
          r.id,
          r.date,
          r.day,
          getDayStatus(r).label,
          secondsToTime(r.checked_in_hr_in_seconds),
          r.edited_checked_in_hr_in_seconds > 0
            ? secondsToTime(r.edited_checked_in_hr_in_seconds)
            : "",
          "",
        ]
          .map(csvEscape)
          .join(","),
      );
    });
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_bulk_edit_${thisMonth}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ---------- Import ----------
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Please upload a .csv file");
      return;
    }

    try {
      const rows = parseCsv(await file.text());
      if (rows.length < 2) {
        toast.error("File has no data rows");
        return;
      }
      const headers = rows[0].map((h) => h.trim().toLowerCase());
      const idIdx = headers.indexOf("id");
      const timeIdx = headers.indexOf("edited_hours");
      const notesIdx = headers.indexOf("notes");
      if (idIdx === -1 || timeIdx === -1 || notesIdx === -1) {
        toast.error(
          "Invalid file. Columns 'id', 'edited_hours' and 'notes' are required",
        );
        return;
      }

      const recordMap = new Map(records.map((r) => [r.id, r]));
      const updates = {};
      const missingNotes = []; // dates with hours but no note
      const invalidHours = []; // dates with bad hours format
      const otherErrors = []; // unknown id / locked rows
      const dateLabel = (r) => format(new Date(r.date), "dd MMM");

      rows.slice(1).forEach((cells, i) => {
        const line = i + 2;
        const id = Number(String(cells[idIdx] || "").trim());
        const timeRaw = String(cells[timeIdx] || "").trim();
        const noteRaw =
          notesIdx > -1 ? String(cells[notesIdx] || "").trim() : "";

        const r = recordMap.get(id);
        if (!r) {
          otherErrors.push(`Row ${line}: unknown id`);
          return;
        }
        if (!canEdit(r)) {
          otherErrors.push(`${dateLabel(r)}: locked`);
          return;
        }
        if (!timeRaw) return; // nothing to import for this row
        const time = normalizeTime(timeRaw);
        if (!time || timeToSeconds(time) <= 0) {
          invalidHours.push(`${dateLabel(r)} ("${timeRaw}")`);
          return;
        }
        if (!noteRaw && !commonNotes.trim()) {
          missingNotes.push(dateLabel(r));
          return;
        }
        updates[id] = { time, notes: noteRaw };
      });

      const importedCount = Object.keys(updates).length;
      if (importedCount > 0) {
        setEdits((prev) => {
          const next = { ...prev };
          Object.entries(updates).forEach(([id, u]) => {
            next[id] = {
              selected: true,
              time: u.time,
              notes: u.notes || prev[id]?.notes || "",
            };
          });
          return next;
        });
        // Show the success toast only when there are no errors
        if (
          missingNotes.length === 0 &&
          invalidHours.length === 0 &&
          otherErrors.length === 0
        ) {
          toast.success(
            `Imported ${importedCount} record(s). Review and click Submit`,
          );
        }
      }

      const list = (arr) =>
        `${arr.slice(0, 5).join(", ")}${arr.length > 5 ? ` +${arr.length - 5} more` : ""}`;

      if (missingNotes.length > 0) {
        toast.error(
          `Note is required. Not imported: ${list(missingNotes)}. Fill the notes column and import again`,
        );
      }
      if (invalidHours.length > 0) {
        toast.error(`Invalid hours (use HH:MM): ${list(invalidHours)}`);
      }
      if (otherErrors.length > 0) {
        toast.error(`Skipped: ${list(otherErrors)}`);
      }
      if (
        importedCount === 0 &&
        missingNotes.length === 0 &&
        invalidHours.length === 0 &&
        otherErrors.length === 0
      ) {
        toast.warning("No edited hours found in the file");
      }
    } catch (err) {
      toast.error("Failed to read file");
    }
  };

  const viewNotes = (r) => {
    setNotesList(r?.notes || []);
    setNotesDate(r?.date);
    setNotes_modal(true);
  };

  // Submit what is set on the rows
  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Select at least one record");
      return;
    }
    if (commonDirty && (commonTime || commonNotes.trim())) {
      toast.warning("Click Apply to set the common hours/notes first");
      return;
    }

    const payloadRecords = [];
    for (const id of selectedIds) {
      const e = edits[id];
      const r = records.find((x) => x.id === id);
      const seconds = timeToSeconds(e.time);
      if (seconds <= 0) {
        toast.error(`Enter valid hours for ${r?.date}`);
        return;
      }
      // Note is mandatory: row note, or the common note as fallback
      if (!(e.notes || "").trim() && !commonNotes.trim()) {
        toast.error(`Note is required for ${r?.date}`);
        return;
      }
      payloadRecords.push({
        id,
        edited_checked_in_hr_in_seconds: seconds,
        notes: (e.notes || "").trim(),
      });
    }

    // Common notes are already on the rows; only send as fallback for empty ones
    const hasEmptyNote = payloadRecords.some((p) => !p.notes);

    setSubmitting(true);
    dispatch(setLoaderVisible(true));
    try {
      const Api = await PostApi(
        "/attendance/bulk-edit/",
        {
          this_month: thisMonth,
          common_notes: hasEmptyNote ? commonNotes.trim() : "",
          records: payloadRecords,
        },
        false,
      );
      if (Api?.status === 200) {
        const updated = Api?.data?.data || [];
        const map = new Map(updated.map((u) => [u.id, u]));
        // POST returns a partial row, so merge over the existing one
        setRecords((prev) =>
          prev.map((r) => (map.has(r.id) ? { ...r, ...map.get(r.id) } : r)),
        );
        setEdits({});
        setCommonNotes("");
        setCommonTime("");
        setCommonDirty(false);
        toast.success(Api?.data?.message || "Attendance edited successfully");
      } else {
        toast.error(
          Api?.data?.message || Api?.data?.detail || "Failed to save",
        );
      }
    } catch (e) {
      toast.error("Failed to save");
    }
    dispatch(setLoaderVisible(false));
    setSubmitting(false);
  };

  document.title = `Bulk Edit Attendance | ${MetaTitle}`;

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <Row className="g-3 align-items-end mb-3">
              <Col md={2}>
                <label className="form-label">Month</label>
                <DatePicker
                  value={dayjs(thisMonth, MonthFormat)}
                  format={MonthFormat}
                  picker="month"
                  allowClear={false}
                  className="form-control"
                  placeholder="Select Month"
                  disabledDate={(current) =>
                    current && current.isAfter(dayjs(), "month")
                  }
                  onChange={(_date, monthStr) =>
                    monthStr && setThisMonth(monthStr)
                  }
                />
              </Col>
              <Col md={2}>
                <label className="form-label">Common Edited Hrs</label>
                <Input
                  type="time"
                  value={commonTime}
                  onChange={(e) => {
                    setCommonTime(e.target.value);
                    setCommonDirty(true);
                  }}
                />
              </Col>
              <Col md={4}>
                <label className="form-label">Common Notes</label>
                <Input
                  type="text"
                  value={commonNotes}
                  placeholder="e.g. Was working from client site"
                  onChange={(e) => {
                    setCommonNotes(e.target.value);
                    setCommonDirty(true);
                  }}
                />
              </Col>
              <Col md={4} className="d-flex gap-2 justify-content-end">
                <Button
                  color="primary"
                  outline
                  disabled={
                    selectedIds.length === 0 ||
                    (!commonTime && !commonNotes.trim())
                  }
                  onClick={applyCommon}
                >
                  Apply to Selected
                </Button>
                <Button
                  color="success"
                  disabled={submitting || selectedIds.length === 0}
                  onClick={handleSubmit}
                >
                  Submit ({selectedIds.length})
                </Button>
              </Col>
            </Row>

            <div className="d-flex gap-2 justify-content-end mb-3">
              <Button
                color="secondary"
                outline
                disabled={editableRows.length === 0}
                onClick={handleExportSample}
              >
                <i className="ri-download-2-line align-middle me-1"></i>
                1. Export Sample
              </Button>
              <Button
                color="secondary"
                outline
                disabled={editableRows.length === 0}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="ri-upload-2-line align-middle me-1"></i>
                2. Import
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={handleImportFile}
              />
            </div>

            <div className="table-responsive">
              <Table className="align-middle table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 40 }}>
                      <Input
                        type="checkbox"
                        checked={allSelected}
                        disabled={editableRows.length === 0}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>Date</th>
                    <th>Day Status</th>
                    <th>First Check In</th>
                    <th>Last Check Out</th>
                    <th>Checked Hrs</th>
                    <th style={{ width: 140 }}>Edited Hrs</th>
                    <th>Paid Hrs</th>
                    <th>HR Status</th>
                    <th style={{ minWidth: 220 }}>Note</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {records.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center text-muted py-4">
                        No records found
                      </td>
                    </tr>
                  )}
                  {records.map((r) => {
                    const editable = canEdit(r);
                    const e = edits[r.id];
                    const selected = !!e?.selected;
                    const day = getDayStatus(r);
                    const hr = HR_STATUS[r.hr_status];

                    return (
                      <tr
                        key={r.id}
                        className={!editable ? "table-light text-muted" : ""}
                      >
                        <td>
                          <Input
                            type="checkbox"
                            checked={selected}
                            disabled={!editable}
                            onChange={() => toggleRow(r)}
                          />
                        </td>
                        <td>
                          <b>{format(new Date(r.date), "dd MMM, yyyy")}</b>
                          <div className="fs-12 text-muted">{r.day}</div>
                        </td>
                        <td>
                          <Badge color={day.color} className="text-uppercase">
                            {day.label}
                          </Badge>
                        </td>
                        <td>
                          {r.first_check_in
                            ? format(ParseTime(r.first_check_in), "h:mm a")
                            : "-"}
                        </td>
                        <td>
                          {r.last_check_out
                            ? format(ParseTime(r.last_check_out), "h:mm a")
                            : "-"}
                        </td>
                        <td>
                          {ConvertSecondsToTime(r.checked_in_hr_in_seconds)}
                        </td>
                        <td>
                          {selected ? (
                            <Input
                              type="time"
                              bsSize="sm"
                              value={e.time}
                              onChange={(ev) =>
                                setField(r.id, "time", ev.target.value)
                              }
                            />
                          ) : (
                            <b>
                              {ConvertSecondsToTime(
                                r.edited_checked_in_hr_in_seconds,
                              )}
                            </b>
                          )}
                        </td>
                        <td>
                          {ConvertSecondsToTime(r.final_paid_hr_in_seconds)}
                        </td>
                        <td>
                          {hr ? (
                            <span
                              className={`badge text-uppercase bg-${hr.color}-subtle text-${hr.color}`}
                            >
                              {hr.label}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          {selected ? (
                            <Input
                              type="text"
                              bsSize="sm"
                              placeholder="Note (required)"
                              invalid={
                                !(e.notes || "").trim() && !commonNotes.trim()
                              }
                              value={e.notes}
                              onChange={(ev) =>
                                setField(r.id, "notes", ev.target.value)
                              }
                            />
                          ) : !r.is_editable ? (
                            <span className="fs-12">Locked by HR</span>
                          ) : r.is_leave_applied ? (
                            <span className="fs-12">Leave applied</span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <a
                            href="#"
                            onClick={(ev) => {
                              ev.preventDefault();
                              ev.stopPropagation();
                              viewNotes(r);
                            }}
                          >
                            {r.notes?.length || 0} Notes
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        <Modal
          isOpen={notes_modal}
          toggle={() => setNotes_modal(false)}
          centered
          className="custom-modal"
        >
          <ModalHeader toggle={() => setNotes_modal(false)}>
            <span style={{ fontSize: "smaller" }}>
              Notes -{" "}
              {notesDate ? format(new Date(notesDate), "dd MMM, yyyy") : ""}
            </span>
          </ModalHeader>
          <ModalBody className="text-left">
            <SimpleBar
              style={{ maxHeight: "300px" }}
              className="px-3 mx-n3 mb-2"
            >
              {notesList.length > 0 ? (
                notesList.map((d, idx) => {
                  const name = d?.employee_name || "Unknown";
                  return (
                    <div className="d-flex mb-4" key={d?.id ?? idx}>
                      <div className="flex-shrink-0">
                        <div
                          className="avatar-xs rounded-circle"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            background: "#0ab39c",
                          }}
                          title={name}
                        >
                          <span
                            className="first_letter"
                            style={{ color: "white" }}
                          >
                            {name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h5 className="fs-13">
                          {name}{" "}
                          <small className="text-muted ms-2">
                            {GetAmPmDateTime(d?.created_time)}
                          </small>
                        </h5>
                        <p className="text-muted mb-0">{d?.message}</p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "25vh",
                  }}
                >
                  <lord-icon
                    src="https://cdn.lordicon.com/msoeawqm.json"
                    trigger="loop"
                    colors="primary:#405189,secondary:#0ab39c"
                    style={{
                      width: "150px",
                      height: "80px",
                      marginBottom: "20px",
                    }}
                  ></lord-icon>
                  <p
                    style={{
                      color: "#405189",
                      fontSize: "20px",
                      textAlign: "center",
                    }}
                  >
                    No Notes Found!
                  </p>
                </div>
              )}
            </SimpleBar>
            <div className="text-center mt-3">
              <Button color="warning" onClick={() => setNotes_modal(false)}>
                Close
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default AttendanceBulkEdit;
